import React from "react";
import { Link } from "gatsby";
import { useCookieConsent } from "react-cookie-manager";

import { ReactComponent as FacebookIcon } from "../images/icons/facebook-footer.svg";
import { ReactComponent as XIcon } from "../images/icons/x-footer.svg";
import { ReactComponent as LinkedInIcon } from "../images/icons/linkedin-footer.svg";
import { ReactComponent as YoutubeIcon } from "../images/icons/youtube-footer.svg";
import { ReactComponent as InstagramIcon } from "../images/icons/instagram-footer.svg";

const SOCIAL_LINKS = {
  facebook:
    process.env.GATSBY_SOCIAL_FACEBOOK ||
    "https://www.facebook.com/MultiquipUk",
  linkedin:
    process.env.GATSBY_SOCIAL_LINKEDIN ||
    "https://www.linkedin.com/company/multiquip-uk-limited",
  twitter: process.env.GATSBY_SOCIAL_TWITTER || "https://x.com/MultiquipUK",
  youtube:
    process.env.GATSBY_SOCIAL_YOUTUBE || "https://www.youtube.com/@MultiquipUK",
  instagram:
    process.env.GATSBY_SOCIAL_INSTAGRAM ||
    "https://www.instagram.com/multiquipuk/",
};

const NEWSLETTER_SIGNUP_URL =
  process.env.GATSBY_NEWSLETTER_SIGNUP_URL || "/contact";

const isExternalUrl = (value) => /^https?:\/\//i.test(String(value || ""));

const Footer = () => {
  const { openPreferencesModal } = useCookieConsent();

  return (
    <footer className="w-full bg-[#575757] text-white border-t-2 border-[#575757]">
      <div className="container flex flex-col gap-4 tablet-lg:gap-10 py-[40px] mobile:py-[48px] tablet-lg:py-[60px] desktop-lg:py-[80px]">
        <div className="flex flex-col gap-[32px] mobile:flex-row mobile:flex-wrap mobile:items-start mobile:justify-between mobile:gap-y-[24px] tablet:flex-nowrap tablet:items-center tablet:justify-between">
          {/* Info Column */}
          <div className="w-full mobile:w-full tablet:w-auto flex flex-col gap-[24px] tablet:flex-row tablet:flex-wrap tablet:items-start tablet:gap-x-[48px] tablet:gap-y-[24px] tablet-lg:flex-nowrap">
            {/* Company Info */}
            <div className="text-_1620 font-work-sans font-normal text-mqgrey leading-[20px] space-y-[8px]">
              <p>© {new Date().getFullYear()} Multiquip (UK) Limited</p>
              <p>Company Number 03544287</p>
              <p className="mb-[8px]">
                <a href="tel:+441613392223" className="text-white">
                  +44 161 339 2223
                </a>
              </p>
            </div>

            {/* Legal Links */}
            <div className="space-y-[8px] tablet:basis-full tablet-lg:basis-auto">
              <Link
                to="/legal-notice"
                className="block text-_1620 font-work-sans font-normal text-white"
              >
                Legal Notice
              </Link>
              <Link
                to="/privacy-policy"
                className="block text-_1620 font-work-sans font-normal text-white"
              >
                Privacy Policy
              </Link>
              <Link
                to="/cookie-policy"
                className="block text-_1620 font-work-sans font-normal text-white"
              >
                Cookie Policy
              </Link>
              <button
                type="button"
                onClick={openPreferencesModal}
                className="block text-_1620 font-work-sans font-normal text-white"
              >
                Cookie Settings
              </button>
              <Link
                to="/terms-conditions"
                className="block text-_1620 font-work-sans font-normal text-white"
              >
                Terms & Conditions
              </Link>
              <a
                href="/sitemap-0.xml"
                className="block text-_1620 font-work-sans font-normal text-white"
              >
                Sitemap
              </a>
            </div>
          </div>

          {/* Newsletter + Social */}
          <div className="w-full mobile:w-full tablet:w-auto flex flex-col gap-[16px] tablet:items-end">
            {isExternalUrl(NEWSLETTER_SIGNUP_URL) ? (
              <a
                href={NEWSLETTER_SIGNUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-white text-[#575757] text-_1424 font-work-sans font-medium py-3 px-[10px] rounded-[8px] hover:bg-mqgrey1 transition-colors h-[48px] w-[275px]"
              >
                SIGN UP TO OUR NEWSLETTER
              </a>
            ) : (
              <Link
                to={NEWSLETTER_SIGNUP_URL}
                className="inline-flex items-center justify-center bg-white text-[#575757] text-_1424 font-work-sans font-medium py-3 px-[10px] rounded-[8px] hover:bg-mqgrey1 transition-colors h-[48px] w-[275px] hover:no-underline"
              >
                SIGN UP TO OUR NEWSLETTER
              </Link>
            )}

            {/* Social Icons */}
            <div className="flex gap-4">
              <a
                href={SOCIAL_LINKS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-[#484848] transition-colors"
                aria-label="Facebook"
              >
                <FacebookIcon className="w-6 h-6" />
              </a>
              <a
                href={SOCIAL_LINKS.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-[#484848] transition-colors"
                aria-label="LinkedIn"
              >
                <LinkedInIcon className="w-6 h-6" />
              </a>
              <a
                href={SOCIAL_LINKS.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-[#484848] transition-colors"
                aria-label="X"
              >
                <XIcon className="w-6 h-6" />
              </a>
              <a
                href={SOCIAL_LINKS.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-[#484848] transition-colors"
                aria-label="YouTube"
              >
                <YoutubeIcon className="w-6 h-6" />
              </a>
              <a
                href={SOCIAL_LINKS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-[#484848] transition-colors"
                aria-label="Instagram"
              >
                <InstagramIcon className="w-6 h-6" />
              </a>
            </div>
          </div>
        </div>
        <p>
          Website design by{" "}
          <a
            href="https://seekmarketingpartners.com"
            target="_blank"
            rel="noopener noreferrer"
            className="underline !text-white transition-colors"
          >
            Seek Marketing Partners
          </a>
        </p>
      </div>
    </footer>
  );
};

export default Footer;
